import { useQuery } from "@tanstack/react-query";
import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import * as SRE from "speech-rule-engine";

// Whoever uses this component MUST ensure they are loading MathJax somehow.
declare global {
  interface Window {
    __MathJax_State__?: {
      isReady: boolean;
      promise: Promise<void>;
    };
    MathJax?: any;
  }
}

type MathProps = (
  | {
      tex?: string;
      mathml?: never;
    }
  | {
      tex?: never;
      mathml?: string;
    }
) & {
  display?: boolean;
  language?: string;
};

const Math = ({ mathml, tex, display = false, language = "en" }: MathProps) => {
  const rootElementRef: React.RefObject<HTMLSpanElement> = useRef(null);
  const __MathJax_State__ = window["__MathJax_State__"];
  const [isReady, setIsReady] = useState(__MathJax_State__?.isReady);

  const generateReadableText = async (
    mathmlSource?: string,
    speechLanguage: string = "en",
  ): Promise<string | null> => {
    let speechText = null;

    try {
      const getSreLocale = (lang: string): string => {
        const langCode = lang.toLowerCase();

        // See packages/question-player/src/utils/languageResolution.ts for supported languages
        const localeMap: Record<string, string> = {
          en: "en",
          es: "es",
          de: "de",
        };

        return localeMap[langCode] || "en";
      };

      const sreLocale = getSreLocale(speechLanguage);

      SRE.setupEngine({
        domain: "mathspeak",
        style: "default",
        locale: sreLocale,
      });

      await SRE.engineReady();

      if (mathmlSource) {
        speechText = SRE.toSpeech(mathmlSource);
      }
    } catch {
      return speechText;
    }
    return speechText;
  };

  const renderMathQuery = useQuery({
    queryKey: ["math-content", tex, mathml, display, language, isReady],
    queryFn: async () => {
      if (!isReady) {
        throw new Error("MathJax not ready");
      }

      if (!rootElementRef.current) {
        throw new Error("Root element ref not available");
      }

      const speechText = await generateReadableText(mathml, language);

      const MathJax = window["MathJax"];
      const mathElement = rootElementRef.current!;

      mathElement.innerHTML = "";
      MathJax.texReset();

      const options = MathJax.getMetricsFor(mathElement);
      options.display = display;

      let renderedNode: HTMLElement;

      if (tex) {
        renderedNode = await MathJax.tex2svgPromise(tex, options);
      } else if (mathml) {
        const converted = mathml.replace(/<math/, `<math display="block"`);
        renderedNode = await MathJax.mathml2svgPromise(converted, options);
      } else {
        throw new Error("No math content provided");
      }

      return {
        speechText,
        renderedNode,
      };
    },
    enabled: Boolean(isReady && (tex || mathml)),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes - math expressions are relatively stable
  });

  useLayoutEffect(() => {
    if (renderMathQuery.data?.renderedNode && rootElementRef.current) {
      const mathElement = rootElementRef.current;

      try {
        while (mathElement.firstChild) {
          mathElement.removeChild(mathElement.firstChild);
        }
      } catch {
        mathElement.innerHTML = "";
      }

      const nodeToAppend = renderMathQuery.data.renderedNode.cloneNode(
        true,
      ) as HTMLElement;
      mathElement.appendChild(nodeToAppend);

      const MathJax = window["MathJax"];
      if (MathJax?.startup?.document) {
        MathJax.startup.document.clear();
        MathJax.startup.document.updateDocument();
      }
    }
  }, [renderMathQuery.data]);

  useEffect(() => {
    if (!isReady) {
      __MathJax_State__?.promise.then(() => {
        setIsReady(true);
      });
    }
  }, [isReady, __MathJax_State__]);

  if (!__MathJax_State__) {
    return null;
  }

  const ariaLabel = renderMathQuery.data?.speechText ?? "math expression";
  const title = renderMathQuery.data?.speechText ?? "math expression";

  const getStatus = () => {
    if (renderMathQuery.isLoading) return "loading";
    if (renderMathQuery.isError) return "error";
    if (renderMathQuery.isSuccess) return "ready";
    return "loading";
  };

  return (
    <span
      ref={rootElementRef}
      title={title}
      aria-label={ariaLabel}
      data-tts-type="mathml"
      data-tts-status={getStatus()}
    ></span>
  );
};

export default Math;
